import moment from 'moment';
import {fork, put, race, select, take, takeEvery} from 'redux-saga/effects';
import {takeFetch} from 'redux/helpers';
import {getWeeks} from 'utils/datetime';
import * as serviceActions from '../../../services/actions';
import * as actions from './actions';
import {createSetCostOfEstimation, getInjector, replaceNaNInfinityWithDash} from '../../../../helpers';

const HASH_CODE_FORMATE = 'YYYY-MM-DD';

const setDisplayHours = user => user.displayTotalHours = replaceNaNInfinityWithDash(Math.round(user.totalHours * 10) / 10);
const setDisplayTotalSp = user => user.displayTotalSp = replaceNaNInfinityWithDash(user.totalSp.toFixed(1));

const updateTeamMembers = (users, startDate, endDate) => {
  const weekOptions = getWeeks({ startTime: startDate, endTime: endDate })
    .map((week, i) => ({ week, id: i, label: `${week.startFormat} - ${week.endFormat}` }));

  const dayOverridesHash = {};
  const durationConverter = getInjector().get('durationService').getConverter();
  for (let i = 0; i < users.length; i++) {
    const user = users[i];
    setDisplayHours(user);
    setDisplayTotalSp(user);
    user.workingTimings = {
      ...user.workingTimings,
      durationObject: durationConverter.fromMinutes(user.workingTimings.workingDayDuration)
    };
    dayOverridesHash[user.userId] = {};
    user.dayOverrides.forEach((d) => {
      dayOverridesHash[user.userId][moment(d.day).format(HASH_CODE_FORMATE)] = d.isWorking;
    });
  }
  for (let i = 0; i < weekOptions.length; i++) {
    const weekStart = moment(weekOptions[i].week.start).startOf('isoWeek');
    const userDays = {};
    const userDaysHash = {};
    for (let j = 0; j < users.length; j++) {
      const user = users[j];
      const weekDays = {};
      const weekDaysHash = {};
      for (let k = 0; k < user.workingTimings.weekDayConfig.length; k++) {
        const config = user.workingTimings.weekDayConfig[k];
        let ind = k;
        const dayOfMonth = moment(weekStart).add(ind, 'd');
        const hash = dayOfMonth.format(HASH_CODE_FORMATE);
        let isWorking = config.isWorking;
        weekDaysHash[hash] = isWorking;
        if (typeof dayOverridesHash[user.userId][hash] === 'boolean') {
          isWorking = dayOverridesHash[user.userId][hash];
        }
        const currentSprint = moment(startDate).isSameOrBefore(dayOfMonth)
          && moment(endDate).isSameOrAfter(dayOfMonth);
        
        weekDays[ind] = { isWorking, dayOfMonth, currentSprint };
      }
      userDays[user.userId] = weekDays;
      userDaysHash[user.userId] = weekDaysHash;
    }
    weekOptions[i].userDays = userDays;
    weekOptions[i].userDaysHash = userDaysHash;
  }

  return {
    weekOptions,
    dayOverridesHash
  };
};

function* getUsers() {
  const { success } = yield takeFetch(serviceActions.api.configuration.sprint.GET_USERS);
  if (success) {
    const { sprintValue } = yield select(state => state.configuration.sprint.fieldset);
    yield put(actions.membersUpdated({
      ...success.data,
      ...updateTeamMembers(success.data.users, sprintValue.startDate, sprintValue.endDate),
    }));
    yield put(actions.recalculateRecommendedCapacity());
  }
}

function* init() {
  while (true) {
    const { sprint } = yield take(actions.INIT.BEGIN);
    const teamOptions = sprint.teams;
    const costOfEstimationModel = getSprintEstimationModel(sprint);
    yield put(actions.init.end({
      teamOptions,
      costOfEstimationModel,
      selectedTeam: sprint.selectedTeam,
      ...updateTeamMembers(sprint.users, sprint.startDate, sprint.endDate),
    }));
    yield put(actions.recalculateRecommendedCapacity());
  }
}

function* setUserDay() {
  while (true) {
    const { user, day } = yield take(actions.SET_USER_DAY.BEGIN);
    const { dayOverridesHash, selectedWeek, weekOptions } = yield select(state => state.configuration.sprint.fieldset);
    const hashKey = day.dayOfMonth.format(HASH_CODE_FORMATE);
    if (selectedWeek.userDaysHash[user.userId][hashKey] === day.isWorking) {
      delete dayOverridesHash[user.userId][hashKey];
    } else {
      dayOverridesHash[user.userId][hashKey] = day.isWorking;
    }
    user.dayOverrides.splice(0, user.dayOverrides.length);
    user.dayOverrides.push(...Object.keys(dayOverridesHash[user.userId]).map(key => ({
      day: key,
      isWorking: dayOverridesHash[user.userId][key],
    })));
    let totalDays = 0;
    for (const week of weekOptions) {
      for(const day of Object.values(week.userDays[user.userId])) {
        if(day.currentSprint )
        {
          const currHash = day.dayOfMonth.format(HASH_CODE_FORMATE);
          let isWorking = day.isWorking 
          if (typeof dayOverridesHash[user.userId][currHash] === 'boolean') {
            isWorking = dayOverridesHash[user.userId][currHash];
          }
          totalDays += day.isWorking;
        }
      }
    }
    const updatedUser = {...user, totalDays};
    yield put(actions.setUserDay.end({ dayOverridesHash, user: updatedUser }));
    yield put(actions.recalculateHours.begin({ user: updatedUser }));
  }
}

function* membersUpdated() {
  while (true) {
    yield race([
       take(actions.SET_START_DATE),
      take(actions.SET_END_DATE),
    ]);
    const { sprintValue: { teamId, startDate, endDate, sprintId } } = yield select(state => state.configuration.sprint.fieldset);
    yield put(serviceActions.api.configuration.sprint.getUsers.request({ teamId, startDate, endDate, sprintId }));
    yield fork(getUsers);
  }
}

function* setWorkingDuration() {
  while (true) {
    const { user } = yield take(actions.SET_WORKING_DURATION.BEGIN);
    const durationConverter = getInjector().get('durationService').getConverter();
    const updatedUser = {
      ...user,
      workingTimings: {
        ...user.workingTimings,
        workingDayDuration: durationConverter.toMinutes(user.workingTimings.durationObject)
      }
    };
    yield put(actions.setWorkingDuration.end({ user: updatedUser }));
    yield put(actions.recalculateHours.begin({ user: updatedUser }));
  }
}

function* recalculateHours() {
  while(true) {
    const { user } = yield take(actions.RECALCULATE_HOURS.BEGIN);
    const updatedUser = {...user, totalHours: user.workingTimings.workingDayDuration / 60 * user.totalDays};
    setDisplayHours(updatedUser);
    yield put(actions.recalculateHours.end({ user: updatedUser }));
    yield put(actions.recalculateSp.begin({ user: updatedUser }));
  }
}

function* watchRecalculateSp() {
  yield takeEvery(actions.RECALCULATE_SP.BEGIN, recalculateSp);
}

function* recalculateSp({ user }) {
  const { costOfEstimationModel } = yield select(state => state.configuration.sprint.fieldset);
  const durationConverter = getInjector().get('durationService').getConverter();
  const costOfEstimationUnit = durationConverter.toMinutes(costOfEstimationModel);
  const updatedUser = {...user, totalSp: (user.totalHours * 60 / costOfEstimationUnit)};
  setDisplayTotalSp(updatedUser);
  yield put(actions.recalculateSp.end({ user: updatedUser }));
  yield put(actions.recalculateRecommendedCapacity());
}

function* afterSetCostOfEstimation() {
    yield fork(recalculateUserSp);
}

function* setCostOfEstimationInherit() {
  while (true) {
    const { costOfEstUnitInherit } = yield take(actions.SET_COST_OF_ESTIMATION_INHERIT.BEGIN);
    const sprint = yield select(state => state.configuration.sprint.fieldset.sprintValue);
    const costOfEstimationModel = getSprintEstimationModel(sprint);
    yield put(actions.setCostInherit.end({ costOfEstUnitInherit, costOfEstimationModel }));
    yield fork(recalculateUserSp);
  }
}

function* resetChanges() {
  yield take(actions.RESET_CHANGES);
  yield put(actions.recalculateRecommendedCapacity());
}

function* recalculateUserSp() {
  const { users } = yield select(state => state.configuration.sprint.fieldset.sprintValue);
  yield users.map(user => put(actions.recalculateSp.begin({ user })));
}

function getSprintEstimationModel(sprint) {
  const durationConverter = getInjector().get('durationService').getConverter();
  return durationConverter.fromMinutes(sprint.costOfEstUnitInherit ?
    sprint.teamCostOfEstimationUnit : sprint.costOfEstimationUnit);
}

export default function* fieldset() {
  yield fork(init);
  yield fork(membersUpdated);
  yield fork(setUserDay);
  yield fork(setWorkingDuration);
  yield fork(recalculateHours);
  yield fork(watchRecalculateSp);
  yield fork(setCostOfEstimationInherit);
  yield fork(resetChanges);
  yield fork(createSetCostOfEstimation(actions, afterSetCostOfEstimation));
}



// WEBPACK FOOTER //
// ../redux/store/configuration/sprint/fieldset/saga.js